// alert
import alert from '@drivesally/sally-ui/lib/libs/alert';

// branch api
import createBranchReducedDeductibleApi from '../../api/createBranchReducedDeductible.api.branch';
import deleteReducedDeductibleApi from '../../api/deleteReducedDeductible.api.branch';
import listReducedDeductibleOptionsApi from '../../api/listReducedDeductibleOptions.api.branch';
import updateReducedDeductibleApi from '../../api/updateReducedDeductible.api.branch';

// branch attributes
import defaultAttribute from '../../attributes/default.attribute.reducedDeductible';
import deductibleAmountAttribute from '../../attributes/deductible_amount.attribute.reducedDeductible';
import descAttribute from '../../attributes/desc.attribute.reducedDeductible';
import nameAttribute from '../../attributes/name.attribute.reducedDeductible';
import rateAttribute from '../../attributes/rate.attribute.reducedDeductible';

// branch components
import BranchReducedDeductibleModal from '../../components/BranchReducedDeductibleModal/BranchReducedDeductibleModal';

// attribute lib
import prepare from '@drivesally/sally-fw/lib/attribute/prepare.attribute';

// error lib
import parseError from '@drivesally/sally-fw/lib/error/parseError';

// propTypes
import PropTypes from 'prop-types';

// react
import React, {Component} from 'react';

const sortRates = (list) =>
  [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));

const withDefaultFlag = (prepared) => ({
  ...prepared,
  default: !!prepared.default,
});

const REDUCED_DEDUCTIBLE_FIELDS = [
  'name',
  'desc',
  'rate',
  'deductible_amount',
];

const emptyFormState = () => ({
  newName: nameAttribute(''),
  newDesc: descAttribute(''),
  newRate: rateAttribute(''),
  newDeductible_amount: deductibleAmountAttribute(''),
  newDefault: defaultAttribute(false),
  editName: nameAttribute(''),
  editDesc: descAttribute(''),
  editRate: rateAttribute(''),
  editDeductible_amount: deductibleAmountAttribute(''),
  editDefault: defaultAttribute(false),
});

class BranchReducedDeductibleModalContainer extends Component {
  static propTypes = {
    branch: PropTypes.object,
    onClose: PropTypes.func,
    visible: PropTypes.bool,
  };

  state = {
    deletingId: null,
    editingId: null,
    loading: false,
    rates: [],
    savingCreate: false,
    savingUpdateId: null,
    togglingDefaultId: null,
    ...emptyFormState(),
  };

  componentDidMount() {
    if (this.props.visible) {
      this.bootstrap();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) {
      this.bootstrap();
    }
    if (prevProps.visible && !this.props.visible) {
      this.setState({
        deletingId: null,
        editingId: null,
        rates: [],
        togglingDefaultId: null,
        ...emptyFormState(),
      });
    }
    if (
      this.props.visible &&
      this.props.branch?.id &&
      prevProps.branch?.id !== this.props.branch?.id
    ) {
      this.loadRates();
    }
  }

  bootstrap = () => {
    this.setState({
      deletingId: null,
      editingId: null,
      ...emptyFormState(),
    });
    this.loadRates();
  };

  loadRates = async () => {
    const {branch} = this.props;
    if (!branch?.id) return;
    this.setState({loading: true});
    try {
      const rates = await listReducedDeductibleOptionsApi({
        sally_branch: branch.id,
      });
      this.setState({rates: sortRates(rates), loading: false});
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      this.setState({loading: false});
    }
  };

  changeNew = (value, key) => {
    if (key === 'name') this.setState({newName: value});
    if (key === 'desc') this.setState({newDesc: value});
    if (key === 'rate') this.setState({newRate: value});
    if (key === 'deductible_amount')
      this.setState({newDeductible_amount: value});
    if (key === 'default') this.setState({newDefault: value});
  };

  changeEdit = (value, key) => {
    if (key === 'name') this.setState({editName: value});
    if (key === 'desc') this.setState({editDesc: value});
    if (key === 'rate') this.setState({editRate: value});
    if (key === 'deductible_amount')
      this.setState({editDeductible_amount: value});
    if (key === 'default') this.setState({editDefault: value});
  };

  startEdit = (rate) => {
    this.setState({
      editingId: rate.id,
      editName: nameAttribute(rate.name || ''),
      editDesc: descAttribute(rate.desc || ''),
      editRate: rateAttribute(rate.rate ?? ''),
      editDeductible_amount: deductibleAmountAttribute(
        rate.deductible_amount ?? ''
      ),
      editDefault: defaultAttribute(!!rate.default),
    });
  };

  cancelEdit = () => {
    this.setState({
      editingId: null,
      editName: nameAttribute(''),
      editDesc: descAttribute(''),
      editRate: rateAttribute(''),
      editDeductible_amount: deductibleAmountAttribute(''),
      editDefault: defaultAttribute(false),
    });
  };

  clearOtherDefaults = async (exceptId) => {
    const {rates} = this.state;
    const others = rates.filter((r) => r.id !== exceptId && r.default);
    await Promise.all(
      others.map((r) => updateReducedDeductibleApi(r.id, {default: false}))
    );
  };

  toggleDefault = async (rate, checked) => {
    const {togglingDefaultId, editingId} = this.state;
    if (togglingDefaultId !== null || editingId !== null) return;

    this.setState({togglingDefaultId: rate.id});
    try {
      if (checked) {
        await this.clearOtherDefaults(rate.id);
        await updateReducedDeductibleApi(rate.id, {default: true});
      } else {
        await updateReducedDeductibleApi(rate.id, {default: false});
      }
      this.setState((prev) => ({
        rates: sortRates(
          prev.rates.map((r) => ({
            ...r,
            default: checked ? r.id === rate.id : r.id === rate.id ? false : r.default,
          }))
        ),
        togglingDefaultId: null,
      }));
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      this.setState({togglingDefaultId: null});
    }
  };

  createRate = async () => {
    const {branch} = this.props;
    const {
      newName,
      newDesc,
      newRate,
      newDeductible_amount,
      newDefault,
      savingCreate,
    } = this.state;
    if (savingCreate || !branch?.id) return;
    this.setState({savingCreate: true});
    try {
      const prepared = prepare(
        {
          name: newName,
          desc: newDesc,
          rate: newRate,
          deductible_amount: newDeductible_amount,
          default: newDefault,
        },
        {field: 'api', required: REDUCED_DEDUCTIBLE_FIELDS}
      );
      const payload = withDefaultFlag(prepared);
      if (payload.default) {
        await this.clearOtherDefaults(null);
      }
      const created = await createBranchReducedDeductibleApi({
        ...payload,
        sally_branch: branch.id,
      });
      this.setState((prev) => ({
        rates: sortRates([
          ...prev.rates.map((r) =>
            created.default ? {...r, default: false} : r
          ),
          created,
        ]),
        newName: nameAttribute(''),
        newDesc: descAttribute(''),
        newRate: rateAttribute(''),
        newDeductible_amount: deductibleAmountAttribute(''),
        newDefault: defaultAttribute(false),
        savingCreate: false,
      }));
      alert.success('Insurance package created');
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      this.setState({savingCreate: false});
    }
  };

  saveEdit = async () => {
    const {
      editingId,
      editName,
      editDesc,
      editRate,
      editDeductible_amount,
      editDefault,
    } = this.state;
    if (!editingId) return;
    this.setState({savingUpdateId: editingId});
    try {
      const prepared = prepare(
        {
          name: editName,
          desc: editDesc,
          rate: editRate,
          deductible_amount: editDeductible_amount,
          default: editDefault,
        },
        {field: 'api', required: REDUCED_DEDUCTIBLE_FIELDS}
      );
      const payload = withDefaultFlag(prepared);
      if (payload.default) {
        await this.clearOtherDefaults(editingId);
      }
      const updated = await updateReducedDeductibleApi(editingId, payload);
      this.setState((prev) => ({
        rates: sortRates(
          prev.rates.map((r) => {
            if (r.id === updated.id) return updated;
            if (updated.default) return {...r, default: false};
            return r;
          })
        ),
        editingId: null,
        editName: nameAttribute(''),
        editDesc: descAttribute(''),
        editRate: rateAttribute(''),
        editDeductible_amount: deductibleAmountAttribute(''),
        editDefault: defaultAttribute(false),
        savingUpdateId: null,
      }));
      alert.success('Insurance package updated');
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      this.setState({savingUpdateId: null});
    }
  };

  deleteRate = async (id) => {
    const {deletingId} = this.state;
    if (deletingId !== null) return;
    this.setState({deletingId: id});
    try {
      await deleteReducedDeductibleApi(id);
      this.setState((prev) => ({
        rates: prev.rates.filter((r) => r.id !== id),
        editingId: prev.editingId === id ? null : prev.editingId,
        deletingId: null,
      }));
      alert.success('Insurance package deleted');
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      this.setState({deletingId: null});
    }
  };

  render() {
    const {visible, onClose, branch} = this.props;
    return (
      <BranchReducedDeductibleModal
        branch={branch}
        cancelEdit={this.cancelEdit}
        changeEdit={this.changeEdit}
        changeNew={this.changeNew}
        createRate={this.createRate}
        deleteRate={this.deleteRate}
        deletingId={this.state.deletingId}
        editDeductible_amount={this.state.editDeductible_amount}
        editDesc={this.state.editDesc}
        editName={this.state.editName}
        editDefault={this.state.editDefault}
        editRate={this.state.editRate}
        editingId={this.state.editingId}
        loading={this.state.loading}
        newDefault={this.state.newDefault}
        newDeductible_amount={this.state.newDeductible_amount}
        newDesc={this.state.newDesc}
        newName={this.state.newName}
        newRate={this.state.newRate}
        onClose={onClose}
        rates={this.state.rates}
        saveEdit={this.saveEdit}
        savingCreate={this.state.savingCreate}
        savingUpdateId={this.state.savingUpdateId}
        startEdit={this.startEdit}
        toggleDefault={this.toggleDefault}
        togglingDefaultId={this.state.togglingDefaultId}
        visible={visible}
      />
    );
  }
}

export default BranchReducedDeductibleModalContainer;
